import { useState } from 'react';
import { Pill, AlertCircle, Heart, FileText, Syringe, Search, ChevronDown, ChevronUp } from 'lucide-react';
import AllergyMultiSelect from '@/components/ui/allergy-multi-select';
import MedicalHistoryMultiSelect from '@/components/ui/medical-history-multi-select';

interface MedicalHistoryStepProps {
  medications: string[];
  otherMedications: string;
  allergies: string[];
  previousHormoneTherapy: string;
  hormoneTherapyDetails: string;
  medicalConditions: string[];
  medicalConditionsOther: string;
  additionalMedicalHistory: string;
  updateFormData: (field: string, value: string | boolean | string[]) => void;
}

interface MedicationItem {
  value: string;
  label: string;
  subtitle?: string;
}

interface MedicationCategory {
  id: string;
  label: string;
  items: MedicationItem[];
}

const COMMON_MEDICATIONS: MedicationCategory[] = [
  {
    id: 'blood_pressure',
    label: 'Blood Pressure',
    items: [
      { value: 'Lisinopril', label: 'Lisinopril', subtitle: 'Prinivil, Zestril' },
      { value: 'Amlodipine', label: 'Amlodipine', subtitle: 'Norvasc' },
      { value: 'Losartan', label: 'Losartan', subtitle: 'Cozaar' },
      { value: 'Metoprolol', label: 'Metoprolol', subtitle: 'Lopressor, Toprol-XL' },
      { value: 'Hydrochlorothiazide', label: 'Hydrochlorothiazide', subtitle: 'HCTZ' },
    ],
  },
  {
    id: 'thyroid',
    label: 'Thyroid',
    items: [
      { value: 'Levothyroxine', label: 'Levothyroxine', subtitle: 'Synthroid, Levoxyl' },
      { value: 'Liothyronine', label: 'Liothyronine', subtitle: 'Cytomel' },
      { value: 'Armour Thyroid', label: 'Armour Thyroid', subtitle: 'Natural desiccated thyroid' },
    ],
  },
  {
    id: 'diabetes',
    label: 'Diabetes',
    items: [
      { value: 'Metformin', label: 'Metformin', subtitle: 'Glucophage' },
      { value: 'Insulin', label: 'Insulin', subtitle: 'Various types' },
      { value: 'Glipizide', label: 'Glipizide', subtitle: 'Glucotrol' },
      { value: 'Empagliflozin', label: 'Empagliflozin', subtitle: 'Jardiance' },
    ],
  },
  {
    id: 'cholesterol',
    label: 'Cholesterol',
    items: [
      { value: 'Atorvastatin', label: 'Atorvastatin', subtitle: 'Lipitor' },
      { value: 'Rosuvastatin', label: 'Rosuvastatin', subtitle: 'Crestor' },
      { value: 'Simvastatin', label: 'Simvastatin', subtitle: 'Zocor' },
    ],
  },
  {
    id: 'mental_health',
    label: 'Mental Health',
    items: [
      { value: 'Sertraline', label: 'Sertraline', subtitle: 'Zoloft' },
      { value: 'Fluoxetine', label: 'Fluoxetine', subtitle: 'Prozac' },
      { value: 'Escitalopram', label: 'Escitalopram', subtitle: 'Lexapro' },
      { value: 'Bupropion', label: 'Bupropion', subtitle: 'Wellbutrin' },
      { value: 'Trazodone', label: 'Trazodone', subtitle: 'Desyrel' },
    ],
  },
  {
    id: 'hormones',
    label: 'Hormones & Birth Control',
    items: [
      { value: 'Estradiol', label: 'Estradiol', subtitle: 'Estrace, Vivelle-Dot' },
      { value: 'Progesterone', label: 'Progesterone', subtitle: 'Prometrium' },
      { value: 'Oral contraceptives', label: 'Oral Contraceptives', subtitle: 'Birth control pills' },
      { value: 'Testosterone', label: 'Testosterone', subtitle: 'Various forms' },
      { value: 'DHEA', label: 'DHEA', subtitle: 'Dehydroepiandrosterone' },
    ],
  },
  {
    id: 'pain_inflammation',
    label: 'Pain & Inflammation',
    items: [
      { value: 'Ibuprofen', label: 'Ibuprofen', subtitle: 'Advil, Motrin' },
      { value: 'Naproxen', label: 'Naproxen', subtitle: 'Aleve' },
      { value: 'Acetaminophen', label: 'Acetaminophen', subtitle: 'Tylenol' },
      { value: 'Gabapentin', label: 'Gabapentin', subtitle: 'Neurontin' },
      { value: 'Prednisone', label: 'Prednisone', subtitle: 'Corticosteroid' },
    ],
  },
  {
    id: 'supplements',
    label: 'Vitamins & Supplements',
    items: [
      { value: 'Vitamin D', label: 'Vitamin D', subtitle: 'D3, Cholecalciferol' },
      { value: 'Vitamin B12', label: 'Vitamin B12', subtitle: 'Cyanocobalamin' },
      { value: 'Iron supplement', label: 'Iron Supplement', subtitle: 'Ferrous sulfate' },
      { value: 'Calcium', label: 'Calcium', subtitle: 'Calcium carbonate/citrate' },
      { value: 'Magnesium', label: 'Magnesium', subtitle: 'Various forms' },
      { value: 'Multivitamin', label: 'Multivitamin', subtitle: 'Various brands' },
      { value: 'Fish oil / Omega-3', label: 'Fish Oil / Omega-3', subtitle: 'EPA/DHA' },
      { value: 'Probiotics', label: 'Probiotics', subtitle: 'Various strains' },
    ],
  },
  {
    id: 'other_common',
    label: 'Other Common Medications',
    items: [
      { value: 'Omeprazole', label: 'Omeprazole', subtitle: 'Prilosec (acid reflux)' },
      { value: 'Pantoprazole', label: 'Pantoprazole', subtitle: 'Protonix (acid reflux)' },
      { value: 'Montelukast', label: 'Montelukast', subtitle: 'Singulair (allergies/asthma)' },
      { value: 'Albuterol', label: 'Albuterol', subtitle: 'ProAir, Ventolin (asthma)' },
      { value: 'Cetirizine', label: 'Cetirizine', subtitle: 'Zyrtec (allergies)' },
      { value: 'Loratadine', label: 'Loratadine', subtitle: 'Claritin (allergies)' },
    ],
  },
];

export default function MedicalHistoryStep({
  medications,
  otherMedications,
  allergies,
  previousHormoneTherapy,
  hormoneTherapyDetails,
  medicalConditions,
  medicalConditionsOther,
  additionalMedicalHistory,
  updateFormData
}: MedicalHistoryStepProps) {
  const [medicationSearch, setMedicationSearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  const toggleMedication = (value: string) => {
    if (medications.includes(value)) {
      updateFormData('medications', medications.filter(m => m !== value));
    } else {
      updateFormData('medications', [...medications, value]);
    }
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const filteredCategories = medicationSearch.trim()
    ? COMMON_MEDICATIONS.map(cat => ({
        ...cat,
        items: cat.items.filter(
          item =>
            item.label.toLowerCase().includes(medicationSearch.toLowerCase()) ||
            (item.subtitle && item.subtitle.toLowerCase().includes(medicationSearch.toLowerCase()))
        ),
      })).filter(cat => cat.items.length > 0)
    : COMMON_MEDICATIONS;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <p className="text-sm font-medium uppercase tracking-wider" style={{ color: '#2FFFC7' }}>
          Step 5 of 7
        </p>
        <h2 className="text-3xl font-light tracking-tight" style={{ color: '#E6E9F0' }}>
          Medical History
        </h2>
        <p className="text-lg" style={{ color: '#C1C9D2' }}>
          Help us understand your health background
        </p>
      </div>

      {/* Current Medications */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Pill className="h-5 w-5" style={{ color: '#2FFFC7' }} />
          <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
            Current Medications
          </h3>
        </div>
        <p className="text-sm mb-3" style={{ color: '#C1C9D2' }}>
          Select all medications, vitamins, and supplements you currently take
        </p>

        {/* Selected medications chips */}
        {medications.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {medications.map((med) => (
              <span
                key={med}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all hover:opacity-80"
                style={{
                  background: 'rgba(47, 255, 199, 0.15)',
                  color: '#2FFFC7',
                  border: '1px solid rgba(47, 255, 199, 0.3)',
                }}
                onClick={() => toggleMedication(med)}
              >
                {med}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </span>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#C1C9D2' }} />
          <input
            type="text"
            value={medicationSearch}
            onChange={(e) => setMedicationSearch(e.target.value)}
            placeholder="Search medications..."
            className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(47, 255, 199, 0.2)',
              color: '#E6E9F0',
            }}
          />
        </div>

        {/* Medication categories */}
        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
          {filteredCategories.map((category) => {
            const isExpanded = expandedCategories.includes(category.id) || medicationSearch.trim() !== '';
            const selectedInCategory = category.items.filter(item => medications.includes(item.value)).length;

            return (
              <div
                key={category.id}
                className="rounded-lg overflow-hidden"
                style={{
                  background: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleCategory(category.id)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left transition-all"
                  style={{ color: '#E6E9F0' }}
                >
                  <span className="text-sm font-medium flex items-center gap-2">
                    {category.label}
                    {selectedInCategory > 0 && (
                      <span
                        className="px-2 py-0.5 rounded-full text-xs"
                        style={{ background: 'rgba(47, 255, 199, 0.15)', color: '#2FFFC7' }}
                      >
                        {selectedInCategory}
                      </span>
                    )}
                  </span>
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4" style={{ color: '#C1C9D2' }} />
                  ) : (
                    <ChevronDown className="h-4 w-4" style={{ color: '#C1C9D2' }} />
                  )}
                </button>

                {isExpanded && (
                  <div className="px-2 pb-2">
                    <div className="grid md:grid-cols-2 gap-2">
                      {category.items.map((item) => (
                        <label
                          key={item.value}
                          className="flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all"
                          style={{
                            background: medications.includes(item.value) ? 'rgba(47, 255, 199, 0.1)' : 'transparent',
                            border: medications.includes(item.value) ? '1px solid rgba(47, 255, 199, 0.3)' : '1px solid transparent',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={medications.includes(item.value)}
                            onChange={() => toggleMedication(item.value)}
                            className="h-4 w-4 rounded accent-[#2FFFC7]"
                          />
                          <div>
                            <span className="text-sm" style={{ color: '#E6E9F0' }}>
                              {item.label}
                            </span>
                            {item.subtitle && (
                              <span className="text-xs ml-1.5" style={{ color: '#9DA9B8' }}>
                                ({item.subtitle})
                              </span>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Other medications */}
        <div className="mt-4">
          <label className="block text-sm font-medium mb-2" style={{ color: '#E6E9F0' }}>
            Other medications not listed above
          </label>
          <textarea
            value={otherMedications}
            onChange={(e) => updateFormData('otherMedications', e.target.value)}
            placeholder="List any other medications, vitamins, or supplements with dosages if known..."
            rows={3}
            className="w-full px-4 py-3 rounded-lg text-sm"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(47, 255, 199, 0.2)',
              color: '#E6E9F0',
              resize: 'vertical',
            }}
          />
        </div>
      </div>

      {/* Allergies */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle className="h-5 w-5" style={{ color: '#2FFFC7' }} />
          <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
            Allergies
          </h3>
        </div>
        <div className="intake-multi-select">
          <AllergyMultiSelect
            dark
            placeholder="Search and select allergies..."
            value={allergies}
            onChange={(next) => updateFormData('allergies', next)}
          />
        </div>
      </div>

      {/* Previous Hormone Therapy */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Syringe className="h-5 w-5" style={{ color: '#2FFFC7' }} />
          <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
            Previous Hormone Therapy
          </h3>
        </div>
        <p className="text-sm mb-3" style={{ color: '#C1C9D2' }}>
          Have you tried hormone replacement therapy (HRT) before?
        </p>
        <div className="space-y-4">
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => updateFormData('previousHormoneTherapy', 'never')}
              className="flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all"
              style={{
                background: previousHormoneTherapy === 'never' ? 'rgba(47, 255, 199, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                border: previousHormoneTherapy === 'never' ? '2px solid #2FFFC7' : '1px solid rgba(255, 255, 255, 0.05)',
                color: '#E6E9F0',
              }}
            >
              No, never tried
            </button>
            <button
              type="button"
              onClick={() => updateFormData('previousHormoneTherapy', 'previous')}
              className="flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all"
              style={{
                background: previousHormoneTherapy === 'previous' ? 'rgba(47, 255, 199, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                border: previousHormoneTherapy === 'previous' ? '2px solid #2FFFC7' : '1px solid rgba(255, 255, 255, 0.05)',
                color: '#E6E9F0',
              }}
            >
              Yes, previously
            </button>
            <button
              type="button"
              onClick={() => updateFormData('previousHormoneTherapy', 'current')}
              className="flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all"
              style={{
                background: previousHormoneTherapy === 'current' ? 'rgba(47, 255, 199, 0.1)' : 'rgba(255, 255, 255, 0.02)',
                border: previousHormoneTherapy === 'current' ? '2px solid #2FFFC7' : '1px solid rgba(255, 255, 255, 0.05)',
                color: '#E6E9F0',
              }}
            >
              Currently using
            </button>
          </div>

          {(previousHormoneTherapy === 'previous' || previousHormoneTherapy === 'current') && (
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#E6E9F0' }}>
                Please provide details (type, dosage, duration, outcome)
              </label>
              <textarea
                value={hormoneTherapyDetails}
                onChange={(e) => updateFormData('hormoneTherapyDetails', e.target.value)}
                placeholder="Example: Used estradiol patch 0.1mg + progesterone 200mg for 6 months. Helped with hot flashes but caused breast tenderness."
                rows={3}
                className="w-full px-4 py-3 rounded-lg text-sm"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(47, 255, 199, 0.2)',
                  color: '#000000',
                  resize: 'vertical',
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Medical Conditions */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Heart className="h-5 w-5" style={{ color: '#2FFFC7' }} />
          <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
            Medical Conditions
          </h3>
        </div>
        <div className="intake-multi-select">
          <MedicalHistoryMultiSelect
            dark
            placeholder="Select medical conditions..."
            value={{ conditions: medicalConditions, other: medicalConditionsOther }}
            onChange={(next) => {
              updateFormData('medicalConditions', next.conditions);
              updateFormData('medicalConditionsOther', next.other);
            }}
          />
        </div>
      </div>

      {/* Additional Medical History */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <FileText className="h-5 w-5" style={{ color: '#2FFFC7' }} />
          <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>
            Additional Medical History
          </h3>
        </div>
        <textarea
          value={additionalMedicalHistory}
          onChange={(e) => updateFormData('additionalMedicalHistory', e.target.value)}
          placeholder="Any other relevant medical history, surgeries, or health concerns..."
          rows={4}
          className="w-full px-4 py-3 rounded-lg text-sm"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(47, 255, 199, 0.2)',
            color: '#000000',
            resize: 'vertical',
          }}
        />
      </div>

      {/* HIPAA Notice */}
      <div 
        className="p-4 rounded-lg"
        style={{
          background: 'rgba(47, 255, 199, 0.05)',
          border: '1px solid rgba(47, 255, 199, 0.2)',
        }}
      >
        <p className="text-xs leading-relaxed" style={{ color: '#C1C9D2' }}>
          <strong style={{ color: '#2FFFC7' }}>Privacy & Security:</strong> All medical information is encrypted and stored securely in compliance with HIPAA regulations. Your information will only be shared with your assigned healthcare provider.
        </p>
      </div>
    </div>
  );
}
